import { toast } from "sonner";
import { useContext, useEffect, useMemo, useState } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { Loader2 } from "lucide-react";
import type { HubspotContactFieldMapping, HubspotContactProperty } from "./types";
import HubspotContactFieldsEditor from "./HubspotContactFieldsEditor";

const selectClass =
  "w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500";

type HubspotGlobalVariablesConfigurationProps = {
  crmConnection: any;
};

const HubspotGlobalVariablesConfiguration = ({
  crmConnection,
}: HubspotGlobalVariablesConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const accessToken: string | undefined = crmConnection?.hubspot?.accessToken;

  const [contactProperties, setContactProperties] = useState<HubspotContactProperty[]>([]);
  const [dealProperties, setDealProperties] = useState<HubspotContactProperty[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [phoneUniqueProperty, setPhoneUniqueProperty] = useState("");
  const [usernameUniqueProperty, setUsernameUniqueProperty] = useState("");
  const [contactFieldMappings, setContactFieldMappings] = useState<HubspotContactFieldMapping[]>(
    []
  );
  const [dealFieldMappings, setDealFieldMappings] = useState<HubspotContactFieldMapping[]>([]);
  const [tagProperty, setTagProperty] = useState("");
  const [assignOwner, setAssignOwner] = useState(false);

  const utils = trpc.useUtils();

  // The mappable-attribute catalog, straight from the constant the sync reads.
  // A plain query — side-effect-free and cached for the session, so the dropdown
  // does not refetch per mapping row.
  // Merchant-scoped: Origen, Campaña and Categoría de transferencia carry THIS
  // merchant's configured values, so the value-map editor can offer a picking list.
  const { data: leadAttributes } = trpc.crmSelfConfig.getHubspotLeadAttributes.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const getContactProperties = trpc.crmSelfConfig.getHubspotContactProperties.useMutation({
    onSuccess: (result) => {
      if (result.success) setContactProperties(result.data as HubspotContactProperty[]);
    },
  });

  const getDealProperties = trpc.crmSelfConfig.getHubspotDealProperties.useMutation({
    onSuccess: (result) => {
      if (result.success) setDealProperties(result.data as HubspotContactProperty[]);
    },
  });

  const updateHubspot = trpc.crmSelfConfig.updateHubspot.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración guardada", {
        description: "Las variables globales se guardaron correctamente.",
      });
      setIsSaving(false);
      setIsEditing(false);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al guardar la configuración.",
      });
      setIsSaving(false);
    },
  });

  useEffect(() => {
    setPhoneUniqueProperty(crmConnection?.hubspot?.phoneUniqueProperty || "");
  }, [crmConnection?.hubspot?.phoneUniqueProperty]);

  useEffect(() => {
    setUsernameUniqueProperty(crmConnection?.hubspot?.usernameUniqueProperty || "");
  }, [crmConnection?.hubspot?.usernameUniqueProperty]);

  useEffect(() => {
    setContactFieldMappings(crmConnection?.hubspot?.contactFieldMappings || []);
  }, [crmConnection?.hubspot?.contactFieldMappings]);

  useEffect(() => {
    setDealFieldMappings(crmConnection?.hubspot?.dealFieldMappings || []);
  }, [crmConnection?.hubspot?.dealFieldMappings]);

  useEffect(() => {
    setTagProperty(crmConnection?.hubspot?.palommaTag?.property || "");
  }, [crmConnection?.hubspot?.palommaTag?.property]);

  useEffect(() => {
    setAssignOwner(!!crmConnection?.hubspot?.assignOwner);
  }, [crmConnection?.hubspot?.assignOwner]);

  useEffect(() => {
    if (!isEditing || !accessToken) return;
    if (contactProperties.length === 0) getContactProperties.mutate({ apiKey: accessToken });
    if (dealProperties.length === 0) getDealProperties.mutate({ apiKey: accessToken });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, accessToken]);

  // Only a property HubSpot marks as unique is a legal `idProperty` for
  // batch/upsert — offering any other one just moves a 400 from here to sync time.
  const uniqueProperties = useMemo(
    () => contactProperties.filter((property) => property.hasUniqueValue),
    [contactProperties]
  );

  // The tag is meant to be a HubSpot boolean ("casilla de verificación única"),
  // because the runtime always writes `true`. We still let any property be picked —
  // a merchant may have made theirs an enumeration — but we say so inline rather
  // than letting them find out from a contact that reads `palomma = true` as text.
  const selectedTagProperty = useMemo(
    () => contactProperties.find((property) => property.name === tagProperty),
    [contactProperties, tagProperty]
  );

  if (crmConnection?.provider !== "hubspot") return <></>;

  const handleSave = () => {
    if (!merchantId || !crmConnection?.id) return;
    setIsSaving(true);
    updateHubspot.mutate({
      merchantId,
      id: crmConnection.id,
      phoneUniqueProperty: phoneUniqueProperty || undefined,
      usernameUniqueProperty: usernameUniqueProperty || undefined,
      // Drop half-filled rows so we never persist a property with no target.
      contactFieldMappings: contactFieldMappings.filter((m) => m.leadAttr && m.hubspotProperty),
      dealFieldMappings: dealFieldMappings.filter((m) => m.leadAttr && m.hubspotProperty),
      // `null` clears it. No value to send — the tag is a flag, so the runtime
      // always writes "true".
      palommaTag: tagProperty ? { property: tagProperty } : null,
      assignOwner,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setPhoneUniqueProperty(crmConnection?.hubspot?.phoneUniqueProperty || "");
    setUsernameUniqueProperty(crmConnection?.hubspot?.usernameUniqueProperty || "");
    setContactFieldMappings(crmConnection?.hubspot?.contactFieldMappings || []);
    setDealFieldMappings(crmConnection?.hubspot?.dealFieldMappings || []);
    setTagProperty(crmConnection?.hubspot?.palommaTag?.property || "");
    setAssignOwner(!!crmConnection?.hubspot?.assignOwner);
  };

  const storedMappings: HubspotContactFieldMapping[] =
    crmConnection?.hubspot?.contactFieldMappings || [];
  const storedDealMappings: HubspotContactFieldMapping[] =
    crmConnection?.hubspot?.dealFieldMappings || [];
  const storedTag = crmConnection?.hubspot?.palommaTag;
  const storedAssignOwner = !!crmConnection?.hubspot?.assignOwner;

  const hasConfiguration =
    !!crmConnection?.hubspot?.phoneUniqueProperty ||
    !!crmConnection?.hubspot?.usernameUniqueProperty ||
    storedMappings.length > 0 ||
    storedDealMappings.length > 0 ||
    !!storedTag ||
    storedAssignOwner;
  const propertiesLoading = getContactProperties.isLoading;

  const renderUniquePropertySelect = (value: string, onValueChange: (v: string) => void) => {
    if (propertiesLoading) {
      return (
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p className="text-xs text-gray-500">Cargando propiedades...</p>
        </div>
      );
    }
    if (uniqueProperties.length === 0) {
      return (
        <p className="text-xs text-orange-600">
          Sin propiedades de valor único. Crea una en HubSpot (Propiedades del contacto → marcar
          "Requerir valores únicos") y vuelve a abrir esta sección.
        </p>
      );
    }
    return (
      <select className={selectClass} value={value} onChange={(e) => onValueChange(e.target.value)}>
        <option value="">Selecciona una propiedad</option>
        {uniqueProperties.map((property) => (
          <option key={property.name} value={property.name}>
            {property.label} ({property.name})
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="mb-2 text-lg font-bold">Variables globales</p>
            <p className="text-sm text-gray-600">
              Configuración de HubSpot que aplica a todo el comercio: cómo se deduplican los
              contactos, qué datos del lead se estampan en ellos y en sus negocios, y con qué tag se marcan.
            </p>
          </div>
          <div className="flex flex-row gap-2">
            {hasConfiguration ? (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Configurado
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                Sin configurar
              </span>
            )}
            {!isEditing && (
              <Button variant="main" size="sm" onClick={() => setIsEditing(true)}>
                Editar
              </Button>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="flex flex-col gap-4">
            {/* ---- Deduplicación de contactos ---- */}
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-sm font-bold text-gray-700">Deduplicación de contactos</p>
                <p className="text-xs text-gray-500">
                  Selecciona las propiedades que HubSpot usa como valor único para no duplicar
                  contactos: una para el teléfono y otra para el usuario de WhatsApp. Solo se listan
                  propiedades marcadas como valor único en HubSpot.
                </p>
              </div>

              <div className="flex flex-col gap-1">
                <Label className="text-sm font-semibold text-gray-700">
                  Propiedad de teléfono (valor único) *
                </Label>
                {renderUniquePropertySelect(phoneUniqueProperty, setPhoneUniqueProperty)}
              </div>

              <div className="flex flex-col gap-1">
                <Label className="text-sm font-semibold text-gray-700">
                  Propiedad de usuario de WhatsApp (valor único)
                </Label>
                <p className="text-xs text-gray-500">
                  Se usa para deduplicar leads sin teléfono a partir de su usuario de WhatsApp
                  (@usuario).
                </p>
                {renderUniquePropertySelect(usernameUniqueProperty, setUsernameUniqueProperty)}
              </div>
            </div>

            <Separator />

            {/* ---- Mapeo de campos de contacto ---- */}
            <HubspotContactFieldsEditor
              mappings={contactFieldMappings}
              onChange={setContactFieldMappings}
              properties={contactProperties}
              isLoadingProperties={propertiesLoading}
              leadAttributes={leadAttributes ?? []}
            />

            <Separator />

            <div className="flex flex-col gap-2">
              <HubspotContactFieldsEditor
                object="deal"
                mappings={dealFieldMappings}
                onChange={setDealFieldMappings}
                properties={dealProperties}
                isLoadingProperties={getDealProperties.isLoading}
                leadAttributes={leadAttributes ?? []}
              />
              <p className="text-xs text-gray-500">
                El nombre, el pipeline, la etapa y el propietario del negocio los define Palomma: una
                fila hacia esas propiedades se ignora. Si HubSpot rechaza un campo mapeado (por
                ejemplo, una opción que no existe en un desplegable), el negocio se actualiza igual
                sin los campos mapeados y el error queda en el registro de sincronización del lead.
              </p>
            </div>

            <Separator />

            {/* ---- Propietario ---- */}
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-bold text-gray-700">Propietario en HubSpot</p>
                <p className="text-xs text-gray-500">
                  Asigna el contacto y el negocio de HubSpot al asesor que tiene el lead en
                  Palomma. Los cruzamos por correo: si el correo del asesor en Palomma es el
                  mismo de su usuario en HubSpot, queda asignado solo — no hay nada que mapear,
                  y los asesores nuevos funcionan desde el primer lead.
                </p>
              </div>

              <div className="flex max-w-md items-center gap-3">
                <Switch checked={assignOwner} onCheckedChange={setAssignOwner} />
                <Label className="text-sm text-gray-700">Asignar propietario según el asesor</Label>
              </div>

              {assignOwner && (
                <p className="text-xs text-gray-500">
                  Se reasigna en cada sincronización, así que si transfieres el lead a otro asesor
                  en Palomma, HubSpot lo sigue. Si el asesor no tiene correo, o su correo no
                  corresponde a un usuario de HubSpot, dejamos el propietario como esté: nunca se
                  pierde la sincronización del lead por esto.
                </p>
              )}
            </div>

            <Separator />

            {/* ---- Tag de Palomma ---- */}
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-bold text-gray-700">Tag de Palomma</p>
                <p className="text-xs text-gray-500">
                  Marca en HubSpot los contactos originados en Palomma, para que puedas filtrarlos y
                  segmentarlos allá. Selecciona la propiedad y listo: la marcamos en cada
                  sincronización del lead.
                </p>
              </div>

              {propertiesLoading ? (
                <div className="flex items-center gap-2">
                  <Spinner size="sm" />
                  <p className="text-xs text-gray-500">Cargando propiedades...</p>
                </div>
              ) : (
                <div className="flex max-w-md flex-col gap-1">
                  <Label className="text-xs">Propiedad de HubSpot</Label>
                  <select
                    className={selectClass}
                    value={tagProperty}
                    onChange={(e) => setTagProperty(e.target.value)}
                  >
                    <option value="">Sin tag</option>
                    {contactProperties.map((property) => (
                      <option key={property.name} value={property.name}>
                        {property.label} ({property.name})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {!!selectedTagProperty && selectedTagProperty.type !== "bool" && (
                <p className="text-xs text-orange-600">
                  "{selectedTagProperty.label}" no es una propiedad booleana (es de tipo{" "}
                  {selectedTagProperty.type}). Se le escribirá el texto "true", que probablemente no
                  es lo que esperas.
                </p>
              )}
            </div>

            <div className="mt-2 flex flex-row gap-2">
              <Button variant="main" size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="size-4 animate-spin" />}
                Guardar
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 text-xs">
            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-gray-700">Deduplicación de contactos</p>
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-600">Propiedad de teléfono:</p>
                <p className="text-gray-700">
                  {crmConnection?.hubspot?.phoneUniqueProperty || "Sin configurar"}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-600">Propiedad de usuario de WhatsApp:</p>
                <p className="text-gray-700">
                  {crmConnection?.hubspot?.usernameUniqueProperty || "Sin configurar"}
                </p>
              </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-gray-700">Mapeo de campos de contacto</p>
              {storedMappings.length === 0 ? (
                <p className="text-gray-500">Sin campos mapeados</p>
              ) : (
                storedMappings.map((mapping, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <p className="font-medium text-gray-600">{mapping.leadAttr}</p>
                    <p className="text-gray-700">{mapping.hubspotProperty}</p>
                  </div>
                ))
              )}
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-gray-700">Mapeo de campos del negocio</p>
              {storedDealMappings.length === 0 ? (
                <p className="text-gray-500">Sin campos mapeados</p>
              ) : (
                storedDealMappings.map((mapping, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <p className="font-medium text-gray-600">{mapping.leadAttr}</p>
                    <p className="text-gray-700">{mapping.hubspotProperty}</p>
                  </div>
                ))
              )}
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-gray-700">Propietario en HubSpot</p>
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-600">Asignar según el asesor:</p>
                <p className="text-gray-700">{storedAssignOwner ? "Sí" : "No"}</p>
              </div>
            </div>

            <Separator />

            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-gray-700">Tag de Palomma</p>
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-600">Propiedad marcada:</p>
                <p className="text-gray-700">{storedTag?.property || "Sin configurar"}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HubspotGlobalVariablesConfiguration;
